// src/resources/dashboards/ProductEvolution/sections/TicketSection.tsx
//
// O ticket por ORIGEM é o argumento comercial da PontuaX — "quem abastece pelo
// app gasta mais" — e ninguém media. Fica no topo da seção, com a diferença
// entre app e pista calculada em texto, porque é isso que vai pra reunião.
//
// As alavancas vêm com benchmark de carteira. O campo `disclaimer` do backend é
// renderizado LITERALMENTE em cada card: mediana de quem usa × de quem não usa
// gera hipótese comercial, não mede efeito, e a UI não pode deixar essa frase
// de fora.
import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { COLORS, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum } from '../../shared';
import type { SectionState } from '../../shared';
import { FillinOrigin } from '../../../../models';
import { ChainTicketData } from '../types';
import {
  BackendNote, Card, MetricBar, OnOffChip, ShowingCount, Stat, StatGrid,
  fmtBrlPrecise, fmtPctSmall, quintileText,
} from './kit';

const HEIGHT = 640;

/** Altura máxima da tabela de postos: rola dentro do card, sem cortar linha. */
const PLACES_MAX_HEIGHT = 320;

const ORIGIN_COLOR: Record<string, string> = {
  [FillinOrigin.app]: COLORS.brand,
  [FillinOrigin.autoSystem]: COLORS.blue,
  [FillinOrigin.externalApi]: COLORS.teal,
  [FillinOrigin.pumpX]: COLORS.purple,
  [FillinOrigin.manuallyAdded]: COLORS.faint,
};

const Th = ({ children, align = 'right' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <TableCell align={align} sx={{
    borderBottom: '1px solid rgba(120,105,223,0.12)', whiteSpace: 'nowrap', py: 0.75,
    fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: COLORS.muted,
    position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1,
  }}>
    {children}
  </TableCell>
);

const Td = ({ children, align = 'right', bold }: { children: React.ReactNode; align?: 'left' | 'right'; bold?: boolean }) => (
  <TableCell align={align} sx={{
    borderBottom: '1px solid rgba(148,163,184,0.16)', py: 0.75, whiteSpace: 'nowrap',
    fontSize: 12.5, fontWeight: bold ? 700 : 400, color: COLORS.ink,
  }}>
    {children}
  </TableCell>
);

const LeverCard = ({ lever }: { lever: ChainTicketData['levers'][number] }) => {
  const { benchmark } = lever;
  const hasBenchmark = benchmark.withLever !== null && benchmark.withoutLever !== null;
  const delta = benchmark.deltaPct;
  return (
    <Box sx={{
      p: 1.5, borderRadius: 3, border: '1px solid rgba(148,163,184,0.24)',
      bgcolor: lever.enabled ? 'rgba(34,197,94,0.04)' : 'rgba(148,163,184,0.05)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: COLORS.ink }}>{lever.label}</Typography>
        <OnOffChip on={lever.enabled} onLabel="ligada" offLabel="desligada" />
      </Box>
      <Typography sx={{ fontSize: 11.5, color: COLORS.muted, lineHeight: 1.5 }}>{lever.hint}</Typography>

      {hasBenchmark ? (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 12, color: COLORS.ink, lineHeight: 1.6 }}>
            Quem usa: <b>{fmtBrl(benchmark.withLever)}</b> · quem não usa: <b>{fmtBrl(benchmark.withoutLever)}</b>
            {delta !== null && (
              <Box component="span" sx={{ ml: 0.75, fontWeight: 800, color: delta >= 0 ? COLORS.success : COLORS.danger }}>
                {delta >= 0 ? '+' : '−'}{fmtPctSmall(Math.abs(delta))}
              </Box>
            )}
          </Typography>
          <Typography sx={{ fontSize: 11, color: COLORS.faint }}>
            amostra: {fmtNum(benchmark.sampleWith)} redes usam · {fmtNum(benchmark.sampleWithout)} não usam
          </Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 11.5, color: COLORS.faint, mt: 1 }}>
          Sem amostra suficiente na carteira para comparar (mínimo de 3 redes de cada lado) —
          {' '}{fmtNum(benchmark.sampleWith)} usam, {fmtNum(benchmark.sampleWithout)} não usam.
        </Typography>
      )}

      {/* Frase obrigatória do backend, literal. */}
      <BackendNote>{benchmark.disclaimer}</BackendNote>
    </Box>
  );
};

const TicketSection = ({ state }: { state: SectionState<ChainTicketData> }) => {
  if (state.loading && !state.data) return <SkeletonCard height={HEIGHT} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={HEIGHT} />;
  if (!state.data) return null;

  const {
    totals, byOrigin, byPlace, levers, recommendations, avgTicketQuintile, portfolioAvgTicketMedian,
  } = state.data;

  const app = byOrigin.find(o => o.origin === FillinOrigin.app) ?? null;
  const pista = byOrigin.find(o => o.origin === FillinOrigin.autoSystem) ?? null;
  // Diferença app × pista: só faz sentido com os dois lados presentes e com
  // ticket de pista > 0 (senão a razão explode).
  const appVsPista = app && pista && pista.avgTicket > 0
    ? (app.avgTicket - pista.avgTicket) / pista.avgTicket
    : null;

  const maxOriginTicket = Math.max(1, ...byOrigin.map(o => o.avgTicket));
  const quintile = quintileText(avgTicketQuintile);
  const activePlaces = byPlace.filter(p => p.fillins > 0);

  return (
    <Box>
      <SectionHeader
        icon={<LocalGasStationIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.brand}, #ec4899)`}
        title="Ticket médio"
        subtitle="Por origem do abastecimento, por posto e as alavancas que a rede tem ligadas"
      />

      <Box sx={{ display: 'grid', gap: 1.75 }}>
        <Box sx={cardSx}>
          <StatGrid min={150}>
            <Stat label="Ticket médio da rede" value={fmtBrl(totals.avgTicket)} color={COLORS.brand}
              sub={quintile ?? 'sem quintil (amostra insuficiente)'} />
            <Stat label="Mediana da carteira" value={fmtBrl(portfolioAvgTicketMedian)}
              sub="entre as redes com movimento" />
            <Stat label="Abastecimentos" value={fmtNum(totals.fillins)} sub={fmtBrl(totals.revenue)} />
            <Stat label="Litros / abastecimento" value={`${fmtNum(totals.litersPerFillin, 1)} L`}
              sub={`${fmtBrlPrecise(totals.pricePerLiter)}/L`} />
          </StatGrid>
        </Box>

        <Card
          title="Ticket por origem do abastecimento"
          hint="É o argumento comercial: quem abastece identificado pelo app deixa mais no caixa?"
        >
          {!byOrigin.length ? (
            <EmptyHint text="Nenhum abastecimento no período — sem origem para comparar." />
          ) : (
            <>
              {appVsPista !== null && (
                <Box sx={{
                  mb: 1.75, px: 1.75, py: 1.25, borderRadius: 3,
                  bgcolor: appVsPista >= 0 ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${appVsPista >= 0 ? 'rgba(34,197,94,0.30)' : 'rgba(239,68,68,0.30)'}`,
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                  <TrendingUpIcon sx={{ fontSize: 18, color: appVsPista >= 0 ? COLORS.success : COLORS.danger }} />
                  <Typography sx={{ fontSize: 12.5, color: COLORS.ink, lineHeight: 1.55 }}>
                    Quem abastece pelo <b>app</b> gasta{' '}
                    <b style={{ color: appVsPista >= 0 ? COLORS.success : COLORS.danger }}>
                      {appVsPista >= 0 ? '+' : '−'}{fmtPctSmall(Math.abs(appVsPista))}
                    </b>{' '}
                    por abastecimento que quem passa pela automação de pista
                    {' '}({fmtBrl(app!.avgTicket)} contra {fmtBrl(pista!.avgTicket)}).
                  </Typography>
                </Box>
              )}

              {byOrigin.map(o => (
                <MetricBar
                  key={o.origin ?? 'sem-origem'}
                  label={o.label}
                  value={`${fmtBrl(o.avgTicket)} · ${fmtNum(o.fillins)} ab. (${fmtPctSmall(o.fillinsShare)})`}
                  pctOfMax={o.avgTicket / maxOriginTicket}
                  color={ORIGIN_COLOR[o.origin ?? ''] ?? COLORS.faint}
                  labelWidth={168}
                />
              ))}

              <Box sx={{ mt: 1.25, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 0.5 }}>
                {byOrigin.map(o => (
                  <Typography key={`d-${o.origin ?? 'null'}`} sx={{ fontSize: 11.5, color: COLORS.muted }}>
                    <b>{o.label}</b>: {fmtNum(o.litersPerFillin, 1)} L/ab · {fmtBrlPrecise(o.pricePerLiter)}/L · {fmtBrl(o.revenue)}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </Card>

        <Card
          title="Ticket por posto"
          hint="Ordenado do maior ticket para o menor"
          right={<ShowingCount shown={activePlaces.length} total={activePlaces.length} noun="posto" />}
        >
          {!activePlaces.length ? (
            <EmptyHint text="Nenhum posto com abastecimento no período." />
          ) : (
            <Box sx={{ maxHeight: PLACES_MAX_HEIGHT, overflow: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <Th align="left">Posto</Th>
                    <Th>Ticket médio</Th>
                    <Th>Abastecimentos</Th>
                    <Th>Receita</Th>
                    <Th>L / ab.</Th>
                    <Th>R$ / L</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activePlaces.map(p => (
                    <TableRow key={p.placeId} hover>
                      <Td align="left" bold>
                        {p.name}
                        {!p.isActive && (
                          <Typography component="span" sx={{ fontSize: 10.5, color: COLORS.faint, ml: 0.75 }}>inativo</Typography>
                        )}
                      </Td>
                      <Td bold>{fmtBrl(p.avgTicket)}</Td>
                      <Td>{fmtNum(p.fillins)}</Td>
                      <Td>{fmtBrl(p.revenue)}</Td>
                      <Td>{fmtNum(p.litersPerFillin, 1)}</Td>
                      <Td>{fmtBrlPrecise(p.pricePerLiter)}</Td>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Card>

        <Card
          title="Alavancas de ticket"
          hint="O que a rede tem ligado hoje, comparado com o resto da carteira"
        >
          {recommendations.length > 0 && (
            <Box sx={{
              mb: 1.75, px: 1.75, py: 1.5, borderRadius: 3,
              bgcolor: 'rgba(120,105,223,0.07)', border: '1px solid rgba(120,105,223,0.22)',
            }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: COLORS.brand, mb: 0.75 }}>
                Conversa comercial sugerida
              </Typography>
              {recommendations.map(r => (
                <Typography key={r.lever} sx={{ fontSize: 12.5, color: COLORS.ink, lineHeight: 1.65 }}>
                  • {r.text}
                </Typography>
              ))}
            </Box>
          )}

          {!levers.length ? (
            <EmptyHint text="Nenhuma alavanca avaliada para esta rede." />
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: 'repeat(3, 1fr)' }, gap: 1.25 }}>
              {levers.map(lever => <LeverCard key={lever.key} lever={lever} />)}
            </Box>
          )}

          <Tooltip arrow title="O quintil vem do ticket de todas as redes com movimento no mesmo período.">
            <Typography sx={{ fontSize: 11.5, color: COLORS.faint, mt: 1.5, display: 'inline-block' }}>
              As sugestões só aparecem quando a rede está nos dois quintis inferiores de ticket e a alavanca está
              desligada. Hoje: {quintile ?? 'quintil indisponível'}.
            </Typography>
          </Tooltip>
        </Card>
      </Box>
    </Box>
  );
};

export default TicketSection;
